import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const privateLogoModules = import.meta.glob('../../assets/private/*.png', {
  eager: true,
  import: 'default',
});

const govtLogoModules = import.meta.glob('../../assets/*.png', {
  eager: true,
  import: 'default',
});

const buildLogoEntries = (modules: Record<string, unknown>) =>
  Object.entries(modules)
    .map(([path, src]) => {
      const fileName = path.split('/').pop() ?? '';
      const name = fileName.replace('.png', '');
      return {
        src: src as string,
        name,
      };
    })
    .filter((logo) => logo.name && logo.name !== '97af715ab6a3414a5586695e213784f67e1e0ed5');

const privateLogos = buildLogoEntries(privateLogoModules);
const govtLogos = buildLogoEntries(govtLogoModules);

const sortedPrivateAsc = [...privateLogos].sort((a, b) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
);
const sortedPrivateDesc = [...sortedPrivateAsc].reverse();

const sortedGovtAsc = [...govtLogos].sort((a, b) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
);
const sortedGovtDesc = [...sortedGovtAsc].reverse();

export const Partners = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const privateRowRef = useRef<HTMLDivElement>(null);
  const govtRowRef = useRef<HTMLDivElement>(null);
  
  const handleViewClients = () => {
    navigate('/clients-journey');
  };

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth < 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const startAutoScroll = (ref: React.RefObject<HTMLDivElement>) => {
      if (!ref.current) return undefined;
      return window.setInterval(() => {
        const el = ref.current;
        if (!el) return;
        const maxScrollLeft = el.scrollWidth - el.clientWidth;
        if (maxScrollLeft <= 0) return;
        const nextLeft = el.scrollLeft + 1.2;
        el.scrollLeft = nextLeft >= maxScrollLeft ? 0 : nextLeft;
      }, 16);
    };

    const privateTimer = startAutoScroll(privateRowRef);
    const govtTimer = startAutoScroll(govtRowRef);

    return () => {
      if (privateTimer) window.clearInterval(privateTimer);
      if (govtTimer) window.clearInterval(govtTimer);
    };
  }, [isMobile]);

  const renderMarquee = (
    logos: Array<{ src: string; name: string }>,
    keyPrefix: string,
    marqueeClassName: string,
    rowRef: React.RefObject<HTMLDivElement>
  ) => (
    <div className="w-full">
      <div className="relative w-full">
        {isMobile && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-between">
            <div className="h-full w-12 bg-gradient-to-r from-gray-50 via-gray-50/80 to-transparent flex items-center justify-start">
              <ChevronLeft className="ml-2 h-5 w-5 text-slate-500" aria-hidden="true" />
            </div>
            <div className="h-full w-12 bg-gradient-to-l from-gray-50 via-gray-50/80 to-transparent flex items-center justify-end">
              <ChevronRight className="mr-2 h-5 w-5 text-slate-500" aria-hidden="true" />
            </div>
          </div>
        )}
        <div
          ref={rowRef}
          className={`relative w-full ${isMobile ? 'overflow-x-auto' : 'overflow-hidden'} `}
          style={isMobile ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : undefined}
        >
          {isMobile && (
            <style>{`div[style*="scrollbarWidth"]::-webkit-scrollbar{display:none;}`}</style>
          )}
        <div
          className={`${isMobile ? '' : marqueeClassName} flex w-max items-center gap-16 py-4 ${isMobile ? 'snap-x snap-mandatory' : ''}`}
        >
          {[...logos, ...logos].map((logo, index) => (
            <div
              key={`${keyPrefix}-${logo.name}-${index}`}
              className={`flex items-center justify-center ${isMobile ? 'snap-center' : ''}`}
            >
              <img
                src={logo.src}
                alt={`Client ${logo.name}`}
                className="h-12 md:h-16 w-auto opacity-80 transition hover:opacity-100"
                loading="lazy"
              />
            </div>
          ))}
        </div>
        </div>
      </div>
    </div>
  );

  return (
    <section id="clients" className="bg-gray-50 scroll-mt-28">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center">
          <p className="text-center text-xl  text-black-800 uppercase tracking-[0.4em] mb-10 font-bold">
            Our Valuable Customers and Partners
          </p>

          <div className="mt-8 flex w-full flex-col gap-6">
            {renderMarquee(sortedPrivateAsc, 'private-asc', 'logo-marquee', privateRowRef)}
            {renderMarquee(sortedGovtDesc, 'govt-desc', 'logo-marquee-reverse', govtRowRef)}
          </div>

          {/* View Clients Button */}
          {/* <div className="mt-12 flex justify-center">
            <button
              onClick={handleViewClients}
              className="px-8 py-4 bg-gradient-to-r from-[#4f46e5] via-[#6366f1] to-[#4f46e5] text-white font-black text-sm md:text-base uppercase tracking-wider rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 border-2 border-[#4f46e5]/30"
            >
              View Clients Journey
            </button>
          </div> */}
        </div>
      </div>
    </section>
  );
};
